import { useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMountEffect } from '@/hooks/use-mount-effect';
import {
  CANVAS_W,
  CANVAS_H,
  createGameState,
  runGameLoop,
  stopGameLoop,
  handleKeyDown,
  handleKeyUp,
  type GameState,
} from './engine';
import * as styles from './styles.css';

const PREVENT_DEFAULT_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' ']);

interface GameModalProps {
  onClose: () => void;
  onGameOver: (score: number, wave: number, isNewHighScore: boolean) => void;
}

export function GameModal({ onClose, onGameOver }: GameModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef<GameState | null>(null);

  useMountEffect(() => {
    const canvas = canvasRef.current;
    if (canvas == null) return;

    const state = createGameState();
    state.onGameOver = onGameOver;
    stateRef.current = state;
    runGameLoop(canvas, state);

    return () => {
      stopGameLoop(state);
    };
  });

  const onKeyDownHandler = useCallback(
    (event: KeyboardEvent) => {
      if (PREVENT_DEFAULT_KEYS.has(event.key)) {
        event.preventDefault();
      }

      if (event.key === 'Escape') {
        const state = stateRef.current;
        if (state === null || state.screen === 'title' || state.screen === 'gameover') {
          onClose();
        } else {
          state.paused = !state.paused;
        }
        return;
      }

      const current = stateRef.current;
      if (current !== null) {
        handleKeyDown(current, event.key);
      }
    },
    [onClose],
  );

  const onKeyUpHandler = useCallback((event: KeyboardEvent) => {
    if (stateRef.current !== null) {
      handleKeyUp(stateRef.current, event.key);
    }
  }, []);

  // eslint-disable-next-line local-rules/prefer-use-mount-effect -- deps change when callbacks update
  useEffect(() => {
    window.addEventListener('keydown', onKeyDownHandler);
    window.addEventListener('keyup', onKeyUpHandler);
    return () => {
      window.removeEventListener('keydown', onKeyDownHandler);
      window.removeEventListener('keyup', onKeyUpHandler);
    };
  }, [onKeyDownHandler, onKeyUpHandler]);

  useMountEffect(() => {
    // Prevent background scrolling while modal is open
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const previous = document.activeElement as HTMLElement | null;
    canvasRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      previous?.focus();
    };
  });

  const handleOverlayClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleContainerClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  return createPortal(
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <div
      className={styles.modalOverlay}
      onClick={handleOverlayClick}
      onKeyDown={undefined}
      role="dialog"
      aria-label="Message Invaders game"
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className={styles.modalContainer} onClick={handleContainerClick}>
        <canvas ref={canvasRef} className={styles.canvas} width={CANVAS_W} height={CANVAS_H} tabIndex={0} />
      </div>
    </div>,
    document.body,
  );
}
